import React from 'react';
import styled from 'styled-components';
import { colors, fonts } from '../../components/annuaire/designSystem';
import LigneBonusRepar from '../../components/annuaire/shared/LigneBonusRepar';
import { horaireLong, majuscule } from './creneaux';
import { CHEMIN_ESPACE } from './espaceSite';
import { BoutonVert, Corps, Pied, SousTitre, Titre, mobile } from './ui';

/**
 * La fin du tunnel de `/reserver/:slug` (maquette validée le 23/09,
 * `widget-confirmation.html`). Même récap que sur la fiche annuaire, avec une
 * proposition en plus, en retrait : retrouver la demande dans un espace
 * Roulez Jeunesse. Elle n'est jamais cochée d'office et ne demande aucun mot
 * de passe ici : cliquer ouvre la page dédiée, avec le jeton de la demande.
 *
 * Roulez Jeunesse ne garantit rien sur la réparation : aucun texte ici ne dit
 * autre chose que « l'atelier va confirmer ».
 *
 * « Créer mon espace » ouvre `/creer-mon-espace` (`espaceSite.js`) : le jeton
 * de la demande y voyage dans le fragment de l'adresse, jamais dans la requête.
 */

/** Le rond plein et sa coche blanche. */
const Coche = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin: 0 auto 4px;
  border-radius: 50%;
  background: ${(p) => p.$couleur};

  svg {
    width: 32px;
    height: 32px;
    stroke: ${colors.white};
  }
`;

const Centre = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 12px;
  text-align: center;
`;

const Recap = styled.dl`
  margin: 6px 0 0;
  padding: 4px 14px;
  border-radius: 16px;
  background: ${colors.grisClair};
  text-align: left;

  div {
    padding: 10px 0;
    border-bottom: 1px solid ${mobile.border};
    font-size: 13.5px;
    line-height: 1.45;
  }
  div:last-child {
    border-bottom: none;
  }
  dt {
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${colors.textSecondary};
  }
  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
`;

const Espace = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-top: 14px;
  padding: 14px;
  border: 1.5px dashed ${colors.borderLight};
  border-radius: 16px;
  text-align: left;

  svg {
    flex: 0 0 auto;
    width: 24px;
    height: 24px;
    stroke: ${colors.textSecondary};
  }
  .texte {
    flex: 1;
    min-width: 0;
    font-size: 13.5px;
    line-height: 1.4;
  }
  .texte b {
    display: block;
    font-weight: 600;
    color: ${colors.textDark};
  }
  .texte span {
    color: ${colors.textSecondary};
  }
  a {
    flex: 0 0 auto;
    padding: 9px 14px;
    border: 1.5px solid ${colors.borderLight};
    border-radius: 50px;
    font-family: ${fonts.body};
    font-size: 13.5px;
    font-weight: 600;
    color: ${colors.textDark};
    text-decoration: none;
    white-space: nowrap;
  }
  a:hover {
    background: ${colors.grisClair};
  }
  a:focus-visible {
    outline: 3px solid ${colors.textDark};
    outline-offset: 2px;
  }

  /* Téléphone étroit : le bouton passe sous le texte plutôt que de l'écraser. */
  @media (max-width: 440px) {
    flex-wrap: wrap;

    .texte {
      flex-basis: calc(100% - 40px);
    }
    a {
      flex: 1 1 100%;
      text-align: center;
    }
  }
`;

/**
 * `resultat` : `'demande'` (à confirmer par l'atelier), `'confirme'` (instantané,
 * code validé), `'creneau-pris'` (le code est bon mais l'horaire a été pris
 * entre-temps : la demande part à l'atelier, qui répond par email) ou
 * `'sans-code'` (instantané dont le code n'est pas arrivé : la demande reste
 * à confirmer par l'atelier, FIXERLIGHT-807).
 */
export default function ConfirmationSite({
  resultat = 'demande',
  couleur,
  email,
  adresse,
  probleme,
  selected,
  bonus = null,
  lienEspace = CHEMIN_ESPACE,
  onRevenir,
  titreRef,
}) {
  const tries = [...selected].sort((a, b) => a.localeCompare(b));
  const confirme = resultat === 'confirme';
  const pris = resultat === 'creneau-pris';
  const sansCode = resultat === 'sans-code';
  const titre = confirme
    ? 'Rendez-vous réservé'
    : pris
    ? 'Cet horaire vient d’être pris'
    : 'Demande envoyée';
  return (
    <>
      <Corps>
        <Centre>
          <Coche $couleur={couleur} aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2.75"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M5 13l4 4L19 7" />
            </svg>
          </Coche>
          <Titre tabIndex={-1} ref={titreRef} style={{ textAlign: 'center' }}>
            {titre}
          </Titre>
          <SousTitre style={{ textAlign: 'center', margin: 0 }}>
            {confirme ? (
              <>
                <b>{majuscule(horaireLong(tries[0]))}.</b>
                <br />
                La confirmation est partie à {email}.
              </>
            ) : pris ? (
              `Votre adresse email est vérifiée, et votre demande est transmise à l’atelier. Il vous répondra par email à ${email}.`
            ) : sansCode ? (
              `Votre demande est enregistrée, sans code. L’atelier va confirmer votre créneau : vous recevrez alors un email à ${email} avec le jour et l’heure du rendez-vous.`
            ) : (
              `${
                tries.length > 1
                  ? `L’atelier va choisir l’un de vos ${tries.length} créneaux.`
                  : 'L’atelier va confirmer votre créneau.'
              } Vous recevrez alors un email à ${email} avec le jour et l’heure du rendez-vous.`
            )}
          </SousTitre>
          <Recap>
            <div>
              <dt>Adresse</dt>
              <dd>{adresse}</dd>
            </div>
            <div>
              <dt>Votre problème</dt>
              <dd>{probleme}</dd>
            </div>
            <div>
              <dt>
                {confirme
                  ? 'Créneau'
                  : tries.length > 1
                  ? 'Créneaux proposés'
                  : 'Créneau proposé'}
              </dt>
              <dd>
                {tries.map((iso) => (
                  <div key={iso} style={{ padding: 0, border: 'none' }}>
                    {majuscule(horaireLong(iso))}
                  </div>
                ))}
              </dd>
            </div>
            <LigneBonusRepar paliers={bonus} />
          </Recap>
        </Centre>
        <Espace>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
          </svg>
          <div className="texte">
            <b>
              {confirme
                ? 'Retrouvez ce rendez-vous'
                : 'Retrouvez cette demande'}
            </b>
            <span>
              {confirme
                ? 'Suivez-le depuis votre espace, quand vous voulez.'
                : 'Suivez-la depuis votre espace, quand vous voulez.'}
            </span>
          </div>
          <a href={lienEspace} target="_blank" rel="noopener noreferrer">
            Créer mon espace
          </a>
        </Espace>
      </Corps>
      <Pied>
        <BoutonVert type="button" onClick={onRevenir}>
          Revenir au site
        </BoutonVert>
      </Pied>
    </>
  );
}
